import { useEffect, useLayoutEffect, useState } from "react";
import { X, ArrowRight, ArrowLeft } from "@phosphor-icons/react";

// Lightweight spotlight walkthrough. Each step points at an element by
// data-testid selector; if the element is missing the tip is centred.
export const DesignerTour = ({ steps = [], onClose }) => {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const step = steps[i];

  useLayoutEffect(() => {
    const measure = () => {
      const el = step?.selector ? document.querySelector(step.selector) : null;
      if (el) {
        el.scrollIntoView({ block: "nearest", inline: "nearest" });
        setRect(el.getBoundingClientRect());
      } else {
        setRect(null);
      }
    };
    measure();
    const t = setTimeout(measure, 80);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [i, step]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowRight")
        setI((n) => (n < steps.length - 1 ? n + 1 : n));
      if (e.key === "ArrowLeft") setI((n) => (n > 0 ? n - 1 : n));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [steps.length, onClose]);

  if (!step) return null;

  const pad = 8;
  const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const tipW = Math.min(320, vw - 24);
  let top;
  let left;
  if (rect) {
    const placeBelow = rect.bottom + 200 < vh;
    top = placeBelow ? rect.bottom + 14 : Math.max(12, rect.top - 200);
    left = Math.min(Math.max(12, rect.left), vw - tipW - 12);
  } else {
    top = vh / 2 - 100;
    left = vw / 2 - tipW / 2;
  }

  const last = i >= steps.length - 1;

  return (
    <div
      className="fixed inset-0 z-[120]"
      style={{ pointerEvents: "none" }}
      data-testid="designer-tour"
    >
      {rect ? (
        <div
          style={{
            position: "fixed",
            top: rect.top - pad,
            left: rect.left - pad,
            width: rect.width + pad * 2,
            height: rect.height + pad * 2,
            borderRadius: 14,
            boxShadow: "0 0 0 9999px rgba(9,9,11,0.74)",
            border: "2px solid rgba(129,140,248,0.95)",
            transition: "all .2s ease",
          }}
        />
      ) : (
        <div
          className="fixed inset-0"
          style={{ background: "rgba(9,9,11,0.74)" }}
        />
      )}

      <div
        className="fixed rounded-xl border border-indigo-400/40 bg-zinc-900 p-4 shadow-2xl"
        style={{ top, left, width: tipW, pointerEvents: "auto" }}
        data-testid="designer-tour-tip"
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="designer-tour-close"
          className="absolute right-2 top-2 rounded-md p-1 text-zinc-500 transition-colors duration-150 hover:bg-zinc-800 hover:text-zinc-200"
        >
          <X size={14} weight="bold" />
        </button>
        <div className="text-[10px] font-bold uppercase tracking-widest text-indigo-300">
          Step {i + 1} of {steps.length}
        </div>
        <div className="mt-1 pr-5 text-sm font-bold text-zinc-50">
          {step.title}
        </div>
        <p className="mt-1.5 text-xs leading-relaxed text-zinc-400">
          {step.body}
        </p>
        <div className="mt-4 flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="designer-tour-skip"
            className="text-[11px] font-semibold text-zinc-500 transition-colors duration-150 hover:text-zinc-300"
          >
            Skip
          </button>
          <div className="ml-auto flex items-center gap-2">
            {i > 0 && (
              <button
                type="button"
                onClick={() => setI((n) => Math.max(0, n - 1))}
                data-testid="designer-tour-prev"
                className="flex items-center gap-1 rounded-lg border border-zinc-700 px-2.5 py-1.5 text-[11px] font-bold text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
              >
                <ArrowLeft size={12} weight="bold" /> Back
              </button>
            )}
            <button
              type="button"
              onClick={() => (last ? onClose?.() : setI((n) => n + 1))}
              data-testid="designer-tour-next"
              className="flex items-center gap-1 rounded-lg bg-indigo-500 px-3 py-1.5 text-[11px] font-bold text-white shadow-[0_0_18px_-4px_rgba(99,102,241,0.8)] transition-colors duration-150 hover:bg-indigo-400"
            >
              {last ? "Got it" : "Next"}
              {!last && <ArrowRight size={12} weight="bold" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
